<script setup lang="ts">
import { ref, nextTick, onMounted, watch } from "vue";
import { api } from "../../../api/Api";
import Toast from "../../../services/Toast";
import {
  Currency,
  Item,
  ItemPackingPrice,
  WarehouseShelf,
} from "../../../types/models";
import { formatDate, formatNumber, isEmpty } from "../../../utils/CommonFunctions";
import IconComponent from "../common/IconComponent.vue";
import ItemGridSelectItems from "./ItemGridSelectItems.vue";
import { useScreenSize } from "../../../utils/useScreenSize";
import ItemSearchMobile from "./ItemSearchMobile.vue";
import ItemSelectGrid from "./ItemSelectGrid.vue";
import TabComponent from "../form/TabComponent.vue";
import Buttons from "../form/Buttons.vue";
import SearchSelect from "../form/SearchSelect.vue";

const props = defineProps<{
  items?: Item[] | [];
  currency?: Currency
  exchange_rate: number
  readonly: boolean;
  warehouses_id?: number | string | null | undefined;
  allowNewRow: boolean;
  ignoreBatchSerial?: boolean;
  shelfManaged?: boolean;
  shelfs?: WarehouseShelf[] | [];
  receiveMode?: boolean;
}>();

defineEmits<{
  (e: "save"): void;
  (e: "items", items: Item[]): void;
}>();

const itemList = ref<Item[]>([]);
const { isMobile } = useScreenSize();

function addItemFromMobile(item?: Item) {
  if (selectedItem.value && !selectedItem.value.items_id) {
    setItem(item)
    return
  }
  if (item) itemList.value.push(item);
  selectedItem.value = itemList.value[itemList.value.length-1]
  setItem(selectedItem.value)
  mobileEditRowIndex.value = -1
}

function addItemRow(index: number) {
  if (props.readonly || !props.allowNewRow || isMobile.value) return;
  if (index < 0) {
    index = itemList.value.length - 1;
  }

  if (
    itemList.value.length > 0 &&
    isEmpty(itemList.value[itemList.value.length - 1].name)
  ) {
    focusNewRow();
    return;
  }

  const newItem: Item = {
    id: itemList.value.length,
    items_id: "",
    photo: "",
    code: "",
    name: "",
    item_name: "",
    sales_name: "",
    item_types_id: "",
    tracked_by: "Quantity",
    purchase_uom: "",
    purchase_uom_qty: 1,
    sales_uom: "",
    sales_uom_qty: 1,
    flexi_price: false,
    ignore_discount: false,
    is_package: false,
    use_package_price: false,
    is_expiry: false,
    tax_details_id: "",
    inventory_uom_id: "",
    purchase_uom_id: "",
    sales_uom_id: "",
    has_variants: false,
    variants: "",
    tax: "",
    tax_rate: 0,
    purchase_order_items_id: "",
    purchase_goods_receipts_id: "",
    warehouses_id: props.warehouses_id ?? null,
    inventory_uom_qty: 0,
    item_variants_id: "",
    unit_of_measures_id: "",
    unit_of_measure: "",
    on_hand: 0,
    quantity: 1,
    uom_qty: 1,
    inventory_qty: 1,
    cost: 0,
    original_price: 0,
    price: 0,
    selling_price: 0,
    sub_total: 0,
    discount_p: 0,
    discount_a: 0,
    value_before_tax: 0,
    tax_amount: 0,
    net_total: 0,
    remarks: "",
    uid: "",
    batches: [],
    serials: [],
    packing_prices: [],
    quantity_prices: [],
    shelfs: [],
    default_sales_uom_id: "",
    default_sales_uom_qty: 0
  };
  itemList.value.push(newItem);
  focusNewRow();
}

const focusNewRow = () => {
  nextTick(() => {
    const index = itemList.value.length - 1;
    const input = document.getElementById("code_" + index);
    if (input) {
      input.focus();
      (input as HTMLInputElement).select();
    }
  });
};

const showItemsDialog = ref(false);
const mobileEditRowIndex = ref(-1)


const selectedItemIndex = ref(-1);
const selectItems = ref<Item[] | []>([]);
const selectedItem = ref<Item>();
async function handleItemCodeEnter(item: Item, index: number) {
  selectedItemIndex.value = index;
  selectedItem.value = item;
  if (isEmpty(item.code)) return;
  const params = {
    search_query: item.code,
    warehouses_id: props.warehouses_id,
  };
  const response = await api.post(
    "inventory/item-management/items/items",
    params
  );
  if (response.success == true) {
    if (response.data.length == 1) {
      setItem(response.data[0]);
    } else if (response.data.length > 1) {
      selectItems.value = response.data;
      showItemsDialog.value = true;
    } else {
      Toast.show("Item not found", "warning");
    }
  } else {
    Toast.show("Item not found", "warning");
  }
}


const setItem = (result: any, copy: boolean = false) => {
  const item = selectedItem.value;
  console.log(item)
  if (item) {
    item.items_id = result.id;
    item.photo = result.photo;
    item.code = result.code;
    item.name = result.name;
    item.item = result.item;
    item.sales_name = result.sales_name;
    item.item_types_id = result.item_types_id;
    item.is_expiry = result.is_expiry;
    item.has_variants = result.has_variants;
    item.variants = result.variants;
    item.uid = result.uid;
    item.tracked_by = result.tracked_by;

    item.warehouses_id = result.warehouses_id ?? props.warehouses_id;
    item.item_variants_id = result.item_variants_id;
    item.inventory_uom_id = result.inventory_uom_id;
    item.inventory_uom_qty = result.inventory_uom_qty;
    item.purchase_uom_id = result.purchase_uom_id;
    item.purchase_uom = result.purchase_uom;
    item.purchase_uom_qty = result.purchase_uom_qty;
    item.default_sales_uom = result.purchase_uom;
    item.default_sales_uom_id = result.purchase_uom_id;
    item.default_sales_uom_qty = result.purchase_uom_qty;
    item.unit_of_measure = result.unit_of_measure;
    item.on_hand = result.on_hand;
    item.quantity = result.quantity ?? 1;
    item.uom_qty = result.sales_uom_qty;
    item.inventory_qty = result.inventory_qty ?? 1;
    item.shelfs = result.shelfs ?? [];

    item.inventory_qty = result.purchase_uom_qty * item.quantity;

    item.packing_prices = result.packing_prices ?? [];

    item.remarks = "";
    if (!copy) {
      item.batches = result.batches;
      item.serials = result.serials;
    }
    console.log(item.tracked_by, 'item.tracked_by', props.shelfManaged, 'props.shelfManaged', props.ignoreBatchSerial, 'props.ignoreBatchSerial')
    if (
      (item.tracked_by == "Batch" ||
        item.tracked_by == "Serial" ||
        props.shelfManaged) &&
      !props.ignoreBatchSerial
    ) {
      if (item.tracked_by == 'Batch') {
        activeTab.value = 'batch'
        
        if (result.shelfs) {
          item.batches?.forEach(b => {
            b.shelfs = result.shelfs.filter((e: any) => e.warehouses_id == props.warehouses_id && e.item_variants_id == item.item_variants_id)
          });          
        }
      } else if (item.tracked_by == 'Serial') {
        activeTab.value = 'serial'
      } else if (item.shelfs && item.shelfs.length > 0) {
        activeTab.value = 'shelfs'
        if (result.shelfs) {
          item.shelfs = result.shelfs.filter((e: any) => e.warehouses_id == props.warehouses_id && e.item_variants_id == item.item_variants_id)
        }
      }
      expandedRows.value.add(String(item.id));
      console.log(expandedRows.value, 'expandedRows.value')
    }

    console.log(item)

    showItemsDialog.value = false;
    // Focus the quantity input
    nextTick(() => {
      const input = document.getElementById(
        "quantity_" + selectedItemIndex.value
      );
      if (input) {
        input.focus();
        (input as HTMLInputElement).select();
      }
    });
  }
};

const removeItem = (index: number) => {
  itemList.value.splice(index, 1)
}

const addItemShelfRow = (item: Item) => {
  if (props.readonly) return;
  item?.shelfs?.push({
    id: null,
    quantity: 0,
    shelf_name: '',
    warehouse_shelfs_id: null,
    sales_invoice_items_id: null,
    warehouses_id: null,
    items_id: String(item.id),
    item_variants_id: item.item_variants_id ? String(item.item_variants_id) : null,
    on_hand: null,
    code: null,
    name: null,
    status_id: null
  });
};
function handleQuantityEnter(item: Item, index: number) {
  item.inventory_qty = (item.quantity ?? 1) * (item.purchase_uom_qty ?? 1);
  if ((item.batches?.length ?? 0) > 1 && !props.ignoreBatchSerial) {
    if (item.tracked_by == "Batch" && item.batches && item.batches.length > 0) {
      let q = item.inventory_qty ?? 1; 
      for(let i = 0; i < item.batches.length; i++) {
        if ((item.batches[i].on_hand ?? 0) > q) {
          item.batches[i].quantity = q;
          q = 0;
        } else {
          item.batches[i].quantity = item.batches[i].on_hand ?? 0;
          q -= item.batches[i].on_hand ?? 0;
        }
        if (q <= 0) break;
      }
    }
  }
  nextTick(() => {
    var input = null;
    if (!props.ignoreBatchSerial) {
      if (
        itemList.value[index].tracked_by == "Batch" &&
        (item.batches?.length ?? 0) > 1
      ) {
        input = document.getElementById(
          "batch_quantity_" + item.items_id + "_" + 0
        );
      } else if (itemList.value[index].tracked_by == "Serial") {
        input = document.getElementById(
          "serial_quantity_" + item.items_id + "_" + 0
        );
      } else if (props.shelfManaged) {
        input = document.getElementById(
          "shelf_quantity_" + item.items_id + "_" + 0
        );
      } else {
        addItemRow(index);
      }
    } else {
      addItemRow(index);
    }
    if (input) {
      input.focus();
      (input as HTMLInputElement).select();
    }
  });
}

const handleReceiveQuantityEnter = (item: Item, index: number) => {
  console.log('handleReceiveQuantityEnter', item, index)
}

const getShelfSum = (item: Item): number => {
  return (
    item.shelfs?.reduce((sum: number, shelf: WarehouseShelf) => {
      const quantity = Number(shelf.quantity);
      if (!isNaN(quantity)) {
        return sum + quantity;
      }
      return sum;
    }, 0) ?? 0
  );
};

const getBatchSum = (item: Item): number => {
  return (
    item.batches?.reduce((sum: number, batch: { quantity: number }) => {
      if (typeof batch.quantity === "number") {
        return sum + batch.quantity;
      }
      return sum;
    }, 0) ?? 0
  );
};

const getSerialSum = (item: Item): number => {
  return (
    item.serials?.reduce((sum: number, serial: { quantity: number }) => {
      if (typeof serial.quantity === "number") {
        return sum + serial.quantity;
      }
      return sum;
    }, 0) ?? 0
  );
};

function handleShelfQuantityEnter(item: Item, batchIndex: number) {
  const qty = getShelfSum(item);
  if (qty == item.inventory_qty) {
    addItemRow(itemList.value.length - 1);
  } else {
    nextTick(() => {
      const input = document.getElementById(
        "shelf_quantity_" + item.items_id + "_" + (batchIndex + 1)
      );
      if (input) {
        input.focus();
        (input as HTMLInputElement).select();
      }
    });
  }
}

function handleBatchQuantityEnter(item: Item, batchIndex: number) {
  const qty = getBatchSum(item);
  if (qty == item.inventory_qty) {
    addItemRow(itemList.value.length - 1);
  } else {
    nextTick(() => {
      const input = document.getElementById(
        "batch_quantity_" + item.items_id + "_" + (batchIndex + 1)
      );
      if (input) {
        input.focus();
        (input as HTMLInputElement).select();
      }
    });
  }
}

function handleSerialQuantityEnter(item: Item, serialIndex: number) {
  const qty = getSerialSum(item);
  if (qty == item.inventory_qty) {
    addItemRow(itemList.value.length - 1);
  } else {
    nextTick(() => {
      const input = document.getElementById(
        "serial_quantity_" + item.items_id + "_" + (serialIndex + 1)
      );
      if (input) {
        input.focus();
        (input as HTMLInputElement).select();
      }
    });
  }
}

// Add expanded items tracking
const expandedRows = ref<Set<string>>(new Set());

function toggleItemExpand(item: Item) {
  if (expandedRows.value.has(String(item.id))) {
    expandedRows.value.delete(String(item.id));
  } else {
    expandedRows.value.add(String(item.id));
  }
}

function isExpanded(item: Item): boolean {
  return expandedRows.value.has(String(item.id));
}

const showItemGridList = ref(false)
const handleSelectedItems = async (items: Item[]) => {
  items.forEach(async item => {
    let hasItem = itemList.value.find((e) => e.code == item.code)
    if (hasItem) {
      selectedItem.value = hasItem
      setItem(item, true)
    } else {
      addItemFromMobile(item)
    }
  });
}

const packingEditRowIndex = ref(-1);

const updatePackingPrice = (item: Item, packing: ItemPackingPrice) => {
  item.purchase_uom_id = packing.unit_of_measures_id;
  item.purchase_uom = packing.unit_of_measure;
  item.purchase_uom_qty = packing.inventory_qty;
  item.on_hand = item.on_hand ? Math.floor(item.on_hand / packing.inventory_qty) : 0;
  item.price = packing.selling_price;
  item.inventory_qty = item.purchase_uom_qty * item.quantity;
  packingEditRowIndex.value = -1;
  //updateLineTotal(item);
};

const clearPackingPrice = (item: Item) => {
  item.purchase_uom_id = item.default_sales_uom_id;
  item.purchase_uom = item.unit_of_measure;
  item.purchase_uom_qty = item.default_sales_uom_qty;
  item.price = item.original_price;
  packingEditRowIndex.value = -1;
  item.inventory_qty = item.purchase_uom_qty * item.quantity;
  //updateLineTotal(item);
};

const closeItemSelect = () => {
  showItemsDialog.value = false;
  selectItems.value = [];
};

watch(
  () => props.items,
  (newValue) => {
    if (newValue) {
      itemList.value = props.items ?? [];
    }
  },
  { immediate: true }
);

watch(
  () => props.allowNewRow,
  (newValue) => {
    if (newValue == true) {
      addItemRow(-1);
    }
  },
  { immediate: true }
);

const activeTab = ref("shelfs");

defineExpose({ addItemRow, handleSelectedItems });

onMounted(() => {
  itemList.value = props.items ?? [];
});
</script>

<template>
  <div class="mb-8">
    <div
      v-if="!isMobile"
      class="relative overflow-hidden border border-gray-200 dark:border-gray-700 rounded-lg"
    >
    <div class="flex justify-between items-center mb-4" v-if="!readonly && allowNewRow">
      <div></div>
      <div>
      <button
        @click="showItemGridList = true"
        
        class="px-4 py-2 mr-2 text-sm font-medium text-white bg-green-600 rounded-lg hover:bg-blue-700"
      >
        Item Grid
      </button>
      <button
        @click="addItemRow(-1)"
        v-if="!readonly"
        class="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700"
      >
        Add Item Row
      </button>
    </div>
    </div>
      <div class="overflow-x-auto overflow-y-auto max-h-[500px]">
        <table class="w-full">
          <thead class="bg-white dark:bg-gray-900 border-b border-gray-200 dark:border-gray-700">
            <tr>
              <th
                class="sticky left-0 z-20 px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase whitespace-nowrap"
              >
                Code
              </th>
              <th
                class="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase whitespace-nowrap"
              >
                Name
              </th>
              <th  v-if="!receiveMode"
                class="px-4 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase whitespace-nowrap"
              >
                On Hand
              </th>
              <th
                class="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase whitespace-nowrap"
              >
                UOM
              </th>
              <th
                class="px-4 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase whitespace-nowrap"
              >
                UOM Qty
              </th>
              <th
                class="px-4 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase whitespace-nowrap"
              >
                Quantity
              </th>
              <th
                class="px-4 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase whitespace-nowrap"
              >
                Inventory Qty
              </th>
              <th v-if="receiveMode"
                class="px-4 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase whitespace-nowrap"
              >
                Accepted
              </th>
              <th v-if="receiveMode"
                class="px-4 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase whitespace-nowrap"
              >
                Remaining
              </th>
              <th v-if="receiveMode"
                class="px-4 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase whitespace-nowrap"
              >
                Receive Qty
              </th>
              <th v-if="receiveMode && readonly"
                class="px-4 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase whitespace-nowrap"
              >
                Balance
              </th>
              <th class="sticky right-0 z-20 px-4 py-3 w-[20px]" v-if="!readonly && !receiveMode"></th>
            </tr>
          </thead>
          <tbody class="divide-y divide-gray-200 dark:divide-gray-700">
            <template v-for="(item, index) in itemList" :key="item.id">
              <tr>
                <!-- Fixed left column -->
                <td class="sticky left-0 z-10 px-4 py-3 w-[200px] min-w-60">
                  <div class="flex items-center">
                    <button
                      @click="toggleItemExpand(item)"
                      class="mr-2"
                      v-if="
                        (item.batches?.length ?? 0) > 0 ||
                        (item.serials?.length ?? 0) > 0
                      "
                    >
                      <IconComponent
                        :name="isExpanded(item) ? 'ArrowUp' : 'ArrowDown'"
                      />
                    </button>
                    <div class="relative" v-if="!props.readonly && !receiveMode">
                      <input
                        :id="'code_' + index"
                        v-model="item.code"
                        type="text"
                        class="w-full pl-3 pr-10 py-2 rounded-lg border border-gray-200 dark:border-gray-700"
                        @keyup.enter="handleItemCodeEnter(item, index)"
                      />
                      <button
                        @click="showItemsDialog = true"
                        class="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                        title="Search items"
                      >
                        <IconComponent name="Search" />
                      </button>
                    </div>
                    <span v-else>{{ item.code }}</span>
                  </div>
                </td>
                <td class="px-4 py-3 min-w-80">{{ item.name ?? item.item }}</td>
                <td class="px-4 py-3 text-right" v-if="!receiveMode">{{ item.on_hand }}</td>
                <td class="px-4 py-3 relative group min-w-40 align-middle w-[150px]">
                  <div
                  v-if="packingEditRowIndex == index"
                  class="right-0 mt-1 w-48 bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 py-1 z-20"
                  >
                  <button
                    v-for="p in item.packing_prices"
                    @click="updatePackingPrice(item, p)"
                    :key="p.unit_of_measures_id"
                    class="w-full px-4 py-2 text-sm text-left text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700"
                  >
                    {{ p.unit_of_measure }} @
                    {{ formatNumber(p.selling_price) }}
                  </button>
                  </div>
                  <span class="align-middle">
                  {{ item.purchase_uom ?? item.unit_of_measure ?? item.unit_of_measure }} x
                  {{ formatNumber((item.purchase_uom_qty ?? item.uom_qty), 2, 0) }} pcs
                  </span>
                  <button
                  v-if="(item.packing_prices?.length ?? 0) > 0 && packingEditRowIndex != index && !readonly && item.default_sales_uom_id == item.purchase_uom_id"
                  @click="packingEditRowIndex = index"
                  class="absolute bottom-3 right-1 p-1 text-xs text-blue-600 dark:text-blue-400 hover:text-gray-700 dark:hover:text-gray-300 align-middle"
                  title="Change Price"
                  >
                  <IconComponent name="PackageSearch" />
                  </button>
                  <button
                  v-if="
                    packingEditRowIndex != index &&
                    !readonly &&
                    item.purchase_uom_id != item.default_sales_uom_id
                  "
                  @click="clearPackingPrice(item)"
                  class="absolute bottom-3 right-1 p-1 text-xs text-blue-600 dark:text-blue-400 hover:text-red-700 dark:hover:text-red-300 align-middle"
                  title="Clear"
                  >
                  <IconComponent name="Trash2" />
                  </button>
                  <button
                  v-if="packingEditRowIndex == index && !readonly"
                  @click="packingEditRowIndex = -1"
                  class="absolute bottom-3 right-1 p-1 text-xs text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 align-middle"
                  title="Cancel"
                  >
                  <IconComponent name="Ban" />
                  </button>
                </td>
                <td class="px-4 py-3 text-right">{{ item.purchase_uom_qty ?? item.uom_qty }}</td>
                <td class="px-4 py-3 text-right">
                  <input
                    v-if="!props.readonly && !receiveMode"
                    :id="'quantity_' + index"
                    v-model.number="item.quantity"
                    type="number"
                    min="1"
                    class="w-24 px-3 py-2 rounded-lg border border-gray-200 dark:border-gray-700 text-right"
                    @keyup="() => item.inventory_qty = (item.quantity ?? 1) * (item.purchase_uom_qty ?? 1)"
                    @keyup.enter="() => handleQuantityEnter(item, index)"
                  />
                  <span v-else>{{ item.quantity }}</span>
                </td>
                <td class="px-4 py-3 text-right">{{ item.inventory_qty }}</td>
                <td class="px-4 py-3 text-right" v-if="receiveMode">{{ item.accepted_qty }}</td>
                <td class="px-4 py-3 text-right" v-if="receiveMode">{{ item.transfer_qty }}</td>
                <td class="px-4 py-3 text-right" v-if="receiveMode">
                  <input
                    v-if="!props.readonly"
                    :id="'quantity_' + index"
                    v-model.number="item.received_qty"
                    @keyup.enter="() => handleReceiveQuantityEnter(item, index)"
                    type="number"
                    min="1"
                    class="w-24 px-3 py-2 rounded-lg border border-gray-200 dark:border-gray-700 text-right"
                  />
                  <span v-else>{{ item.transfer_qty }}</span>
                </td>
                <td class="px-4 py-3 text-right" v-if="receiveMode && readonly">
                  <span>{{ (item.inventory_qty ?? 0) - (item.transfer_qty ?? 0) }}</span>
                </td>
                <!-- Fixed right columns -->
                <td
                 v-if="!readonly && !receiveMode"
                  class="sticky right-0 min-w-36 bg-white dark:bg-gray-900 z-10 px-4 py-3 text-right font-medium flex items-center justify-end"
                >
                  <button
                  v-if="!readonly"
                  @click="removeItem(index)"
                  class="ml-2 text-red-600 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300"
                  >
                  <IconComponent name="Trash2" />
                  </button>
                </td>
              </tr>
              <tr v-if="receiveMode && shelfManaged == true" :key="'expand-receive-shelfs-' + item.id">
                <td
                  colspan="13"
                  class="py-2 px-4 bg-white dark:bg-gray-900 relative"
                >
                <div
                        class="md:flex justify-between items-center mb-6 w-[500px]"
                      >
                        <span
                          class="text-2xl font-medium text-gray-900 dark:text-white"
                          :class="{
                            'bg-red-500 rounded-md p-2':
                              getShelfSum(item) != item.received_qty,
                          }"
                          >Total: {{ getShelfSum(item) }}, should be:
                          {{ item.received_qty }}</span
                        >
                        <Buttons caption="Add Shelf Row" icon="Plus" @click="addItemShelfRow(item)" />
                      </div>
                        <table class="w-[500px]">
                          <thead class="bg-white dark:bg-gray-900 border-b border-gray-200 dark:border-gray-700">
                            <tr>
                              <!--<th
                                class="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase whitespace-nowrap"
                              >
                                Shelf Code
                              </th>-->
                              <!--<th class="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase whitespace-nowrap">Item Type</th>-->
                              <th
                                class="px-4 w-[100px] py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase whitespace-nowrap"
                              >
                                Shelf Name
                              </th>
                              <!--<th
                                class="px-4 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase whitespace-nowrap"
                              >
                                On Hand
                              </th>-->
                              <th
                                class="px-4 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase whitespace-nowrap"
                              >
                                Quantity
                              </th>
                              <th class="sticky right-0 z-20 px-4 py-3"></th>
                            </tr>
                          </thead>
                          <tbody
                            class="divide-y divide-gray-200 dark:divide-gray-700"
                          >
                            <template
                              v-for="(shelf, shelfIndex) in item.shelfs"
                              :key="'receive_shelfs_' + item.id"
                            >
                              <tr>
                                <td class="px-4 py-3 w-[250px]" v-if="!props.readonly">
                                  <SearchSelect
                                    v-model="shelf.warehouse_shelfs_id"
                                    :options="props.shelfs?.map(shelf => ({ ...shelf, name: shelf.name ?? '' }))"
                                    :placeholder="'Select Shelf'"
                                    :searchable="true"
                                    :readonly="props.readonly"
                                    @select="
                                      (selected) =>
                                        ((shelf as any).shelf_name = selected.name)
                                    " />
                                </td>
                                <td class="px-4 py-3 w-[150px]" v-if="props.readonly">
                                  {{ shelf.name }}
                                </td>
                                <!--<td class="px-4 py-3 w-[150px] text-right">
                                  {{ formatNumber(shelf.on_hand ?? 0) }}
                                </td>-->
                                <td class="px-4 py-3 text-right">
                                  <input
                                    @keyup.enter="
                                      handleShelfQuantityEnter(item, shelfIndex)
                                    "
                                    v-if="!props.readonly"
                                    :id="
                                      'shelf_quantity_' +
                                      item.items_id +
                                      '_' +
                                      shelfIndex
                                    "
                                    v-model.number="shelf.quantity"
                                    type="number"
                                    min="1"
                                    class="w-24 px-3 py-2 rounded-lg border border-gray-200 dark:border-gray-700 text-right"
                                  />
                                  <span v-else>{{ shelf.quantity }}</span>
                                </td>
                              </tr>
                            </template>
                          </tbody>
                        </table>
                </td>
              </tr>
              <tr v-if="isExpanded(item)" :key="'expand-' + item.id">
                <td
                  colspan="13"
                  class="py-2 px-4 bg-white dark:bg-gray-900 relative"
                >
                  <TabComponent
                    v-model="activeTab"
                    :tabs="[
                      {
                        name: 'shelfs',
                        label: 'Shelfs',
                        visible: props.shelfManaged,
                      },
                      {
                        name: 'batch',
                        label: 'Batches',
                        visible: item.tracked_by == 'Batch',
                      },
                      {
                        name: 'serial',
                        label: 'Serials',
                        visible: item.tracked_by == 'Serial',
                      },
                    ]"
                  >
                    <template #batch>
                      <div
                        class="md:flex justify-between items-center mb-6 w-[500px]"
                      >
                        <span
                          class="text-2xl font-medium text-gray-900 dark:text-white"
                          :class="{
                            'bg-red-500 rounded-md p-2':
                              getBatchSum(item) != item.inventory_qty,
                          }"
                          >Total: {{ getBatchSum(item) }}, should be:
                          {{ item.inventory_qty }}</span
                        >
                      </div>
                      <div>
                        <table class="w-[500px]">
                          <thead class="bg-white dark:bg-gray-900 border-b border-gray-200 dark:border-gray-700">
                            <tr>
                              <th
                                class="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase whitespace-nowrap"
                              >
                                Batch No
                              </th>
                              <th
                                v-if="props.shelfManaged"
                                class="px-4 w-[100px] py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase whitespace-nowrap"
                              >
                                Expiry Date
                              </th>
                              <!--<th class="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase whitespace-nowrap">Item Type</th>-->
                              <th
                                v-if="item.is_expiry == true"
                                class="px-4 w-[100px] py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase whitespace-nowrap"
                              >
                                Expiry Date
                              </th>
                              <th
                                class="px-4 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase whitespace-nowrap"
                              >
                                On Hand
                              </th>
                              <th
                                class="px-4 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase whitespace-nowrap"
                              >
                                Quantity
                              </th>                              
                              <th class="sticky right-0 z-20 px-4 py-3"></th>
                            </tr>
                          </thead>
                          <tbody
                            class="divide-y divide-gray-200 dark:divide-gray-700"
                          >
                            <template
                              v-for="(batch, batchIndex) in item.batches"
                              :key="'batch_' + item.id"
                            >
                              <tr>
                                <td class="px-4 py-3">
                                  {{ batch.batch_no }}
                                </td>
                                <td class="px-4 py-3" v-if="props.shelfManaged">
                                  {{ batch.warehouse_shelf }}
                                </td>
                                <td
                                  class="px-4 py-3 w-[150px]"
                                  v-if="item.is_expiry == true"
                                >
                                  {{ formatDate(batch.expiry_date) }}
                                </td>
                                <td class="px-4 py-3 text-right">
                                  {{ batch.on_hand }}
                                </td>
                                <td class="px-4 py-3 text-right">
                                  <input
                                    @keyup.enter="
                                      handleBatchQuantityEnter(item, batchIndex)
                                    "
                                    v-if="!props.readonly"
                                    :id="
                                      'batch_quantity_' +
                                      item.items_id +
                                      '_' +
                                      batchIndex
                                    "
                                    v-model.number="batch.quantity"
                                    type="number"
                                    min="1"
                                    class="w-24 px-3 py-2 rounded-lg border border-gray-200 dark:border-gray-700 text-right"
                                  />
                                  <span v-else>{{ batch.quantity }}</span>
                                </td>
                              </tr>
                            </template>
                          </tbody>
                        </table>
                      </div>
                    </template>
                    <template #serial>
                      <div
                        class="md:flex justify-between items-center mb-6 w-[500px]"
                      >
                        <span
                          class="text-2xl font-medium text-gray-900 dark:text-white"
                          >Serials</span
                        >
                        <span
                          class="text-2xl font-medium text-gray-900 dark:text-white"
                          :class="{
                            'bg-red-500 rounded-md p-2':
                              getSerialSum(item) != item.inventory_qty,
                          }"
                          >Total: {{ getSerialSum(item) }}, should be:
                          {{ item.inventory_qty }}</span
                        >
                      </div>
                      <div>
                        <table class="w-[500px]">
                          <thead class="bg-white dark:bg-gray-900 border-b border-gray-200 dark:border-gray-700">
                            <tr>
                              <th
                                class="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase whitespace-nowrap"
                              >
                                Serial No
                              </th>
                              <th
                                v-if="props.shelfManaged"
                                class="px-4 w-[100px] py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase whitespace-nowrap"
                              >
                                Shelf
                              </th>
                              <!--<th class="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase whitespace-nowrap">Item Type</th>-->
                              <th
                                v-if="item.is_expiry == true"
                                class="px-4 w-[100px] py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase whitespace-nowrap"
                              >
                                Expiry Date
                              </th>
                              <th class="sticky right-0 z-20 px-4 py-3"></th>
                            </tr>
                          </thead>
                          <tbody
                            class="divide-y divide-gray-200 dark:divide-gray-700"
                          >
                            <template
                              v-for="(serial, serialIndex) in item.serials"
                              :key="'serial_' + item.id"
                            >
                              <tr>
                                <td class="px-4 py-3">
                                  {{ serial.serial_no }}
                                </td>
                                <td class="px-4 py-3" v-if="props.shelfManaged">
                                  {{ serial.warehouse_shelf }}
                                </td>
                                <td
                                  class="px-4 py-3 w-[150px]"
                                  v-if="item.is_expiry == true"
                                >
                                  {{
                                    formatDate(
                                      serial.expiry_date as string | Date
                                    )
                                  }}
                                </td>
                                <td class="px-4 py-3 text-right">
                                  <input
                                    @keyup.enter="
                                      handleSerialQuantityEnter(
                                        item,
                                        serialIndex
                                      )
                                    "
                                    v-if="!props.readonly"
                                    :id="
                                      'serial_quantity_' +
                                      item.items_id +
                                      '_' +
                                      serialIndex
                                    "
                                    v-model.number="serial.quantity"
                                    :min="0"
                                    :max="1"
                                    type="number"
                                    class="w-24 px-3 py-2 rounded-lg border border-gray-200 dark:border-gray-700 text-right"
                                  />
                                  <span v-else>{{ serial.quantity }}</span>
                                </td>
                              </tr>
                            </template>
                          </tbody>
                        </table>
                      </div>
                    </template>
                    <template #shelfs>
                      <div
                        class="md:flex justify-between items-center mb-6 w-[500px]"
                      >
                        <span
                          class="text-2xl font-medium text-gray-900 dark:text-white"
                          :class="{
                            'bg-red-500 rounded-md p-2':
                              getShelfSum(item) != item.inventory_qty,
                          }"
                          >Total: {{ getShelfSum(item) }}, should be:
                          {{ item.inventory_qty }}</span
                        >
                      </div>
                      <div>
                        <table class="w-[500px]">
                          <thead class="bg-white dark:bg-gray-900 border-b border-gray-200 dark:border-gray-700">
                            <tr>
                              <th
                                class="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase whitespace-nowrap"
                              >
                                Shelf Code
                              </th>
                              <!--<th class="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase whitespace-nowrap">Item Type</th>-->
                              <th
                                class="px-4 w-[100px] py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase whitespace-nowrap"
                              >
                                Shelf Name
                              </th>
                              <th
                                class="px-4 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase whitespace-nowrap"
                              >
                                On Hand
                              </th>
                              <th
                                class="px-4 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase whitespace-nowrap"
                              >
                                Quantity
                              </th>
                              <th class="sticky right-0 z-20 px-4 py-3"></th>
                            </tr>
                          </thead>
                          <tbody
                            class="divide-y divide-gray-200 dark:divide-gray-700"
                          >
                            <template
                              v-for="(shelf, shelfIndex) in item.shelfs"
                              :key="'batch_' + item.id"
                            >
                              <tr>
                                <td class="px-4 py-3">
                                  {{ shelf.code }}
                                </td>
                                <td class="px-4 py-3 w-[150px]">
                                  {{ shelf.name }}
                                </td>
                                <td class="px-4 py-3 w-[150px]">
                                  {{ formatNumber(shelf.on_hand ?? 0) }}
                                </td>
                                <td class="px-4 py-3 text-right">
                                  <input
                                    @keyup.enter="
                                      handleShelfQuantityEnter(item, shelfIndex)
                                    "
                                    v-if="!props.readonly"
                                    :id="
                                      'shelf_quantity_' +
                                      item.items_id +
                                      '_' +
                                      shelfIndex
                                    "
                                    v-model.number="shelf.quantity"
                                    type="number"
                                    min="1"
                                    class="w-24 px-3 py-2 rounded-lg border border-gray-200 dark:border-gray-700 text-right"
                                  />
                                  <span v-else>{{ shelf.quantity }}</span>
                                </td>
                              </tr>
                            </template>
                          </tbody>
                        </table>
                      </div>
                    </template>
                  </TabComponent>
                </td>
              </tr>
            </template>
          </tbody>
        </table>
      </div>
    </div>
    <template v-else>
      <ItemSearchMobile
        @showItemGrid="showItemGridList = true"
        :allow-discount="false"
        :allow-new-row="allowNewRow"
        :currency="currency"
        :exchange_rate="exchange_rate"
        :ignore-batch-serial="ignoreBatchSerial"
        :readonly="readonly"
        :shelf-managed="shelfManaged"
        :shelfs="shelfs"
        :warehouses_id="warehouses_id"
        @item="setItem"
        :tax-details="[]"
        @add-item="addItemFromMobile"
      />
      <div class="md:hidden space-y-4" id="table-records">
        <div
          v-for="(item, index) in itemList"
          :key="item.id"
          class="bg-white dark:bg-gray-900 rounded-lg shadow p-4 border border-gray-200 dark:border-gray-700"
        >
          <div class="space-y-2 text-sm">
            <div key="mobile-code" class="flex justify-between">
              <span class="text-gray-500 dark:text-gray-400">Code</span>
              <span class="font-medium">{{ item.code }}</span>
            </div>
            <div key="mobile-code" class="flex justify-between">
              <span class="text-gray-500 dark:text-gray-400">Name</span>
              <span class="font-medium">{{ item.name || item.item}}</span>
            </div>
            <div key="mobile-code" class="flex justify-between">
              <span class="text-gray-500 dark:text-gray-400">Quantity</span>
              <input
                v-if="!props.readonly && mobileEditRowIndex == index"
                :id="'quantity_mobile'"
                v-model.number="item.quantity"
                type="number"
                min="1"
                class="px-3 py-2 max-w-[120-px] w-[120px] rounded-lg border border-gray-200 dark:border-gray-700 text-right"
                @keyup.enter="() => handleQuantityEnter"
              />
              <span v-else class="font-medium">{{ item.quantity }}</span>
            </div>
          </div>
          <div
            class="mt-4 flex justify-end space-x-2 border-t dark:border-gray-700 pt-4"
            id="sales-documents-actions"
            v-if="!readonly"
          >
          <button
              :key="'edit-item=' + item.id"
              v-if="mobileEditRowIndex != index"
              @click="mobileEditRowIndex = index"
              class="[ 'p-2', 'text-warning-600 hover:text-warning-700', 'dark:text-warning-400 dark:hover:text-warning-300' ]"
              title="Edit Item"
            >
              <IconComponent name="Pencil" />
            </button>
            <button
              :key="'edit-item=' + item.id"
              v-if="mobileEditRowIndex == index"
              @click="mobileEditRowIndex = -1"
              class="[ 'p-2', 'text-warning-600 hover:text-warning-700', 'dark:text-warning-400 dark:hover:text-warning-300' ]"
              title="Edit Item"
            >
              <IconComponent name="Save" />
            </button>
            <button
              :key="'remove-item=' + item.id"
              @click="itemList.splice(index, 1)"
              class="[ 'p-2', 'text-warning-600 hover:text-warning-700', 'dark:text-warning-400 dark:hover:text-warning-300' ]"
              title="Remove Item"
            >
              <IconComponent name="Trash2" />
            </button>
          </div>
        </div>
      </div>
    </template>

    <!-- Totals -->
    <ItemGridSelectItems
      v-if="showItemsDialog"
      :items="selectItems"
      :warehouses_id="warehouses_id"
      @close="closeItemSelect"
      @select-many="handleSelectedItems"
      @select="addItemFromMobile"
    />

    <ItemSelectGrid
      v-if="showItemGridList"
      :warehouses_id="props.warehouses_id"
      @close="showItemGridList = false"
      @selected="handleSelectedItems"
    />

  </div>
</template>
